import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { PaymentAllocationFormSchemaValues } from 'src/features/paymentAllocations/utils/formSchema';
import { pricingPoliciesMessages } from 'src/features/servicePlanPricingPolicies/utils/messages';
import { Input } from 'src/registry/ui/Input';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useListPayments } from 'src/features/payments/api/useListPayments';
import { useListFinancialDocuments } from 'src/features/financialDocuments/api/useListFinancialDocuments';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { FINANCIAL_DOCUMENT_TYPE_TRANSLATIONS } from 'src/features/financialDocuments/utils/constants';
import {
    FINANCIAL_DOCUMENT_PAYMENT_STATUS,
    FINANCIAL_DOCUMENT_STATUS,
} from 'src/features/financialDocuments/utils/types';
import { PAYMENT_STATUS } from 'src/features/payments/utils/types';
import { useGetPayment } from 'src/features/payments/api/useGetPayment';
import { useGetFinancialDocument } from 'src/features/financialDocuments/api/useGetFinancialDocument';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';

type PaymentAllocationFormProps = {
    form: UseFormReturn<PaymentAllocationFormSchemaValues>;
    onSubmit: (values: PaymentAllocationFormSchemaValues) => void;
};

function PaymentAllocationForm({ onSubmit, form }: PaymentAllocationFormProps) {
    const { formatMessage, formatNumber } = useIntl();
    const [displayAmount, setDisplayAmount] = React.useState('');
    const { paymentId = '', financialDocumentId = '' } = useParams();

    const [searchFinancialDoc, setSearchFinancialDoc] = React.useState<string>('');

    const isPaymentDashboard = !!paymentId;

    const { data: selectedPayment } = useGetPayment({
        paymentId: paymentId,
        config: { enabled: !!paymentId },
    });

    const { data: selectedFinancialDocument } = useGetFinancialDocument({
        documentId: financialDocumentId,
        config: { enabled: !!financialDocumentId },
    });

    const { data: paymentsData } = useListPayments({
        status: PAYMENT_STATUS.PAID,
        currency: selectedFinancialDocument?.currency,
        allocatable: true,
        customer: selectedFinancialDocument?.customer?.id,
    });
    const { data: financialDocumentsData } = useListFinancialDocuments({
        status: FINANCIAL_DOCUMENT_STATUS.ISSUED,
        currency: selectedPayment?.currency,
        payment_status: [FINANCIAL_DOCUMENT_PAYMENT_STATUS.NONE, FINANCIAL_DOCUMENT_PAYMENT_STATUS.PARTIAL],
        customer: selectedPayment?.customer?.id,
        search: searchFinancialDoc,
    });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPoliciesMessages.amount} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPoliciesMessages.amount)}...`}
                                    value={
                                        displayAmount !== ''
                                            ? displayAmount
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayAmount(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {!isPaymentDashboard && (
                <FormField
                    control={form.control}
                    name="payment"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...paymentsMessages.payment} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={`${formatMessage(paymentsMessages.payment)}...`}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {paymentsData?.results?.map((option) => {
                                                const paymentAmount = formatNumber(option.amount / 100, {
                                                    style: 'currency',
                                                    currency: option.currency,
                                                });
                                                const remainingAmount = formatNumber(option.amount / 100, {
                                                    style: 'currency',
                                                    currency: option.currency,
                                                });
                                                return (
                                                    <SelectItem key={option.id} value={option.id}>
                                                        {`${paymentAmount} (${formatMessage(financialDocumentsMessages.remainingAmount)}: ${remainingAmount}) - ${getFormattedDateTimeForDetailPanel(option.date)}`}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}

            {isPaymentDashboard && (
                <SearchSelectFormField
                    name="financial_document"
                    control={form.control}
                    label={formatMessage(financialDocumentsMessages.financialDocument)}
                    items={financialDocumentsData?.results ?? []}
                    clearable
                    placeholder={`${formatMessage(financialDocumentsMessages.financialDocument)}...`}
                    searchPlaceholder={formatMessage(financialDocumentsMessages.searchFinancialDocument)}
                    emptyTitle={formatMessage(financialDocumentsMessages.noFinancialDocument)}
                    emptyDescription={formatMessage(financialDocumentsMessages.noFinancialDocumentFound)}
                    getItemValue={(d) => d.id}
                    getItemLabel={(doc) => {
                        const typeLabel = formatMessage(FINANCIAL_DOCUMENT_TYPE_TRANSLATIONS[doc.type]);
                        const remainingAmount = doc?.remaining_amount
                            ? formatNumber(doc?.remaining_amount / 100, {
                                  style: 'currency',
                                  currency: doc.currency,
                              })
                            : '';
                        if (doc.number && doc.type)
                            return `${doc.number} - ${typeLabel} (${formatMessage(financialDocumentsMessages.remainingAmount)}: ${remainingAmount})`;
                        if (doc.number) return doc.number;
                        return typeLabel;
                    }}
                    getItemKeywords={(doc) => [doc.number, doc.type]}
                    loadOptions={setSearchFinancialDoc}
                />
            )}
        </Form>
    );
}

export default PaymentAllocationForm;
